<template>
  <multiselect
    class="app-select"
    :model-value="selected"
    :options="shownOptions"
    label="label"
    track-by="value"
    :multiple="multiple"
    :close-on-select="!multiple"
    :clear-on-select="false"
    :searchable="searchable"
    :allow-empty="canClear()"
    :show-labels="showLabels"
    select-label="Select"
    :deselect-label="canClear() ? 'Remove' : 'Selected'"
    :placeholder="placeholder"
    :disabled="disabled"
    :open-direction="openDirection"
    @update:model-value="onSelect"
    @close="$emit('blur')"
  >
    <template #noResult>No results found.</template>
    <template #noOptions>List is empty.</template>
  </multiselect>
</template>

<script>
// Drop-in replacement for a native <select>, registered globally as <app-select>.
//
// - v-model / modelValue is the option's *value* (like a native select), not the
//   option object. With `multiple` it is an array of values.
// - `options` can be primitives, or objects read through `value-key` / `label-key`.
// - Options are alphabetized (placeholder / empty entries stay on top; numbers,
//   statuses, months... are kept in their given order — see utils/sortOptions).
//   Pass `:sort="false"` for any list that has a natural order.
// - `change` mimics a native change event ({ target: { value } }) so existing
//   `@change="handler"` / `$event.target.value` code keeps working unchanged.
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";
import { sortOptions } from "@/utils/sortOptions";

const isEmpty = (v) => v === "" || v === null || v === undefined;
const same = (a, b) => (isEmpty(a) && isEmpty(b)) || String(a) === String(b);

export default {
  name: "AppSelect",

  components: { Multiselect },

  props: {
    modelValue: { type: null, default: "" },
    options: { type: Array, default: () => [] },
    valueKey: { type: String, default: "value" },
    labelKey: { type: String, default: "label" },
    placeholder: { type: String, default: "Select" },
    // Adds a leading "no selection" entry (value "") with this label, e.g. "All".
    emptyLabel: { type: String, default: "" },
    sort: { type: Boolean, default: true },
    multiple: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    searchable: { type: Boolean, default: true },
    // Can the chosen option be removed again (click it)? Yes by default; pass
    // :allow-empty="false" for a select that must always have a value.
    allowEmpty: { type: Boolean, default: undefined },
    openDirection: { type: String, default: "bottom" },
    // The "Select" / "Selected" hint on the right of each option. Turn off for
    // narrow dropdowns (it would cover the option text).
    showLabels: { type: Boolean, default: true },
  },

  emits: ["update:modelValue", "change", "blur"],

  computed: {
    normalized() {
      return (this.options || []).map((option) => {
        if (option !== null && typeof option === "object") {
          return {
            value: this.valueKey ? option[this.valueKey] : option,
            label: String(option[this.labelKey] ?? ""),
            $isDisabled: !!option.$isDisabled,
          };
        }
        return { value: option, label: String(option ?? "") };
      });
    },

    shownOptions() {
      const list = this.sort
        ? sortOptions(this.normalized, "label", { valueKey: "value" })
        : this.normalized;
      return this.emptyLabel
        ? [{ value: "", label: this.emptyLabel }, ...list]
        : list;
    },

    selected() {
      if (this.multiple) {
        const values = Array.isArray(this.modelValue) ? this.modelValue : [];
        return values
          .map((v) => this.shownOptions.find((o) => same(o.value, v)))
          .filter(Boolean);
      }
      return this.shownOptions.find((o) => same(o.value, this.modelValue)) || null;
    },
  },

  methods: {
    canClear() {
      if (this.multiple) return true;
      return this.allowEmpty !== undefined ? this.allowEmpty : true;
    },
    onSelect(selection) {
      const value = this.multiple
        ? (selection || []).map((o) => o.value)
        : selection
          ? selection.value
          : "";
      this.$emit("update:modelValue", value);
      // The option was clicked off again (single mode): the model is cleared, but
      // `change` handlers are not run - they expect a chosen value, as they did with
      // a native select that could not be emptied.
      if (!this.multiple && !selection) return;
      // A native select's event.target.value is always a string; keep that so
      // existing `@change` handlers (=== "5", parseInt...) behave the same.
      const eventValue = this.multiple ? value : isEmpty(value) ? "" : String(value);
      this.$emit("change", {
        target: { value: eventValue },
        value,
        preventDefault() {},
        stopPropagation() {},
      });
    },
  },
};
</script>
